import type { components } from '../../api/schema';
import { businessDate, money, qty, type FormatSettings } from '../../lib/format';
import { dec } from '../sales/lib/decimal';
import type { PrintDoc, PrintRow } from '../sales/print/documents';
import { priceLines, totalRows } from '../sales/print/builders';
import type { Note } from '../returns/api';

type S = components['schemas'];

/** A4 layouts for the documents that had no print view of their own, for WhatsApp PDFs. */
export function quotationDoc(q: S['Quotation'], f: FormatSettings): PrintDoc {
  return {
    title: 'Quotation',
    number: q.number,
    meta: [
      { label: 'Date', value: businessDate(q.docDate, f) },
      ...(q.validUntil ? [{ label: 'Valid until', value: businessDate(q.validUntil, f) }] : []),
      ...(q.reference ? [{ label: 'Reference', value: q.reference }] : []),
    ],
    party: { heading: 'Quotation for', name: q.contact.name, phone: q.contact.phone },
    lines: priceLines(q.lines, f),
    showPrices: true,
    totals: totalRows(q, f),
    notes: q.notes,
    terms: q.terms,
  };
}

export function purchaseOrderDoc(po: S['PurchaseOrder'], f: FormatSettings): PrintDoc {
  return {
    title: 'Purchase order',
    number: po.number,
    status: po.status === 'CANCELLED' ? 'Cancelled' : undefined,
    meta: [
      { label: 'Order date', value: businessDate(po.docDate, f) },
      ...(po.expectedDate ? [{ label: 'Expected', value: businessDate(po.expectedDate, f) }] : []),
      { label: 'Deliver to', value: po.location.name },
      ...(po.reference ? [{ label: 'Reference', value: po.reference }] : []),
    ],
    party: { heading: 'Supplier', name: po.contact.name, phone: po.contact.phone },
    lines: priceLines(po.lines, f),
    showPrices: true,
    totals: totalRows(po, f),
    notes: po.notes,
  };
}

export function goodsReceiptDoc(g: S['GoodsReceipt'], f: FormatSettings): PrintDoc {
  return {
    title: 'Goods receipt',
    number: g.number,
    status: g.status === 'REVERSED' ? 'Reversed' : undefined,
    meta: [
      { label: 'Date', value: businessDate(g.docDate, f) },
      { label: 'Received at', value: g.location.name },
      ...(g.supplierRef ? [{ label: 'Supplier ref.', value: g.supplierRef }] : []),
    ],
    party: { heading: 'Supplier', name: g.contact.name, phone: g.contact.phone },
    lines: g.lines.map((l) => ({
      description: l.description,
      sku: l.sku,
      qty: qty(l.qtyAccepted, f),
      unit: l.unitSymbol,
    })),
    showPrices: false,
    totals: [],
    notes: g.notes,
  };
}

export function supplierBillDoc(b: S['SupplierInvoice'], f: FormatSettings): PrintDoc {
  const totals = totalRows(b, f);
  if (dec(b.amountPaid) > 0n) totals.push({ label: 'Paid', value: `−${money(b.amountPaid, f)}` });
  const cancelled = b.status === 'CANCELLED';
  return {
    title: 'Supplier bill',
    number: b.number,
    status: cancelled ? 'Cancelled' : b.paymentStatus === 'PAID' ? 'Paid' : undefined,
    meta: [
      { label: 'Bill date', value: businessDate(b.docDate, f) },
      { label: 'Due date', value: businessDate(b.dueDate, f) },
      ...(b.supplierInvoiceNumber
        ? [{ label: 'Supplier invoice', value: b.supplierInvoiceNumber }]
        : []),
    ],
    party: { heading: 'Supplier', name: b.contact.name, phone: b.contact.phone },
    lines: priceLines(b.lines, f),
    showPrices: true,
    totals,
    balance: cancelled ? undefined : { label: 'Balance due', value: money(b.outstanding, f) },
    notes: b.notes,
  };
}

export function paymentDoc(p: NonNullable<S['Payment']>, f: FormatSettings): PrintDoc {
  const applied: PrintRow[] = p.allocations
    .filter((a) => !a.reversed)
    .map((a) => {
      const other = a.credit.id === p.id ? a.target : a.credit;
      return { label: other.number ?? other.type, value: money(a.amount, f) };
    });
  return {
    title:
      p.purpose === 'RECEIPT'
        ? 'Payment receipt'
        : p.purpose === 'SUPPLIER_PAYMENT'
          ? 'Remittance advice'
          : 'Refund',
    number: p.number,
    status: p.status === 'VOIDED' ? 'Voided' : undefined,
    meta: [
      { label: 'Date', value: businessDate(p.paymentDate, f) },
      { label: 'Method', value: p.method.name },
      ...(p.reference ? [{ label: 'Reference', value: p.reference }] : []),
    ],
    party: {
      heading: p.direction === 'IN' ? 'Received from' : 'Paid to',
      name: p.contact.name,
      phone: p.contact.phone,
    },
    lines: [],
    showPrices: false,
    totals: [...applied, { label: 'Amount', value: money(p.amount, f), strong: true }],
    notes: p.notes,
  };
}

function returnLines(
  lines: { description: string; sku?: string | null; qty: string; unitSymbol: string }[],
  f: FormatSettings,
) {
  return lines.map((l) => ({
    description: l.description,
    sku: l.sku ?? undefined,
    qty: qty(l.qty, f),
    unit: l.unitSymbol,
  }));
}

export function salesReturnDoc(r: S['SalesReturn'], f: FormatSettings): PrintDoc {
  return {
    title: 'Sales return',
    number: r.number,
    meta: [
      { label: 'Date', value: businessDate(r.docDate, f) },
      { label: 'Invoice', value: r.salesInvoice.number },
      ...(r.creditNote ? [{ label: 'Credit note', value: r.creditNote.number }] : []),
    ],
    party: { heading: 'Customer', name: r.contact.name, phone: r.contact.phone },
    lines: returnLines(r.lines, f),
    showPrices: false,
    totals: r.creditNote
      ? [{ label: 'Credited', value: money(r.creditNote.total, f), strong: true }]
      : [],
    notes: [r.reason, r.notes].filter(Boolean).join('\n\n') || null,
  };
}

export function purchaseReturnDoc(r: S['PurchaseReturn'], f: FormatSettings): PrintDoc {
  const debited = r.debitNotes.reduce((s, n) => s + dec(n.total), 0n);
  return {
    title: 'Purchase return',
    number: r.number,
    meta: [
      { label: 'Date', value: businessDate(r.docDate, f) },
      { label: 'Goods receipt', value: r.goodsReceipt.number },
      { label: 'From', value: r.location.name },
    ],
    party: { heading: 'Supplier', name: r.contact.name, phone: r.contact.phone },
    lines: returnLines(r.lines, f),
    showPrices: false,
    totals:
      r.debitNotes.length > 0
        ? [{ label: 'Debit notes', value: money(String(debited), f), strong: true }]
        : [],
    notes: [r.reason, r.notes].filter(Boolean).join('\n\n') || null,
  };
}

export function noteDoc(n: Note, f: FormatSettings): PrintDoc {
  const credit = n.kind === 'credit';
  return {
    title: credit ? 'Credit note' : 'Debit note',
    number: n.number,
    status: n.status === 'CANCELLED' ? 'Cancelled' : undefined,
    meta: [
      { label: 'Date', value: businessDate(n.docDate, f) },
      { label: credit ? 'Invoice' : 'Bill', value: n.source.number },
    ],
    party: {
      heading: credit ? 'Customer' : 'Supplier',
      name: n.contact.name,
      phone: n.contact.phone,
    },
    lines: [],
    showPrices: false,
    totals: [
      ...n.lines.map((l) => ({ label: l.description, value: money(l.lineTotal, f) })),
      ...(dec(n.taxTotal) > 0n ? [{ label: 'Tax (included)', value: money(n.taxTotal, f) }] : []),
      { label: 'Total', value: money(n.total, f), strong: true },
    ],
    notes: [n.reason, n.notes].filter(Boolean).join('\n\n') || null,
  };
}
